import { useCallback, useEffect, useRef, useState } from 'react';
import { useStoreActions } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error/error';

interface UseRequestOptions<TData> {
  enabled?: boolean;
  debounceMs?: number;
  initialLoading?: boolean;
  showErrorEnabled?: boolean;
  onSuccess?: (data: TData) => void;
  onError?: (error: string) => void;
  dependencies?: readonly unknown[];
}

interface UseRequestResult<TData> {
  isLoading: boolean;
  refetch: () => Promise<void>;
  data: TData | null;
}

interface UseApiRequestContext {
  signal: AbortSignal;
}

function useApiRequest<TData>(
  queryFnOrArray:
    | ((context: UseApiRequestContext) => Promise<TData>)
    | readonly ((context: UseApiRequestContext) => Promise<unknown>)[]
    | (() => Promise<TData>)
    | readonly (() => Promise<unknown>)[],
  options: UseRequestOptions<TData> = { showErrorEnabled: true },
): UseRequestResult<TData> {
  const { enabled = true, debounceMs, initialLoading = false, dependencies = [] } = options;
  const [isLoading, setIsLoading] = useState(initialLoading && enabled);
  const [data, setData] = useState<TData | null>(null);

  const { showError } = useStoreActions(actions => actions.snackbar);

  const abortControllerRef = useRef<AbortController>();
  const debounceTimeoutRef = useRef<number | undefined>(undefined);

  const queryFnRef = useRef(queryFnOrArray);
  const optionsRef = useRef(options);

  queryFnRef.current = queryFnOrArray;
  optionsRef.current = options;

  const fetchData = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading(true);

    const context: UseApiRequestContext = { signal: controller.signal };

    try {
      let result: TData;

      if (Array.isArray(queryFnRef.current)) {
        const results = await Promise.all(
          queryFnRef.current.map(fn =>
            (fn as (ctx: UseApiRequestContext) => Promise<unknown>)(context),
          ),
        );
        result = results as TData;
        setData(result);
      } else {
        const fn = queryFnRef.current as (ctx: UseApiRequestContext) => Promise<TData>;
        result = await fn(context);
        setData(result);
      }

      if (controller.signal.aborted) {
        return;
      }

      optionsRef.current.onSuccess?.(result);
    } catch (err) {
      if (controller.signal.aborted) {
        return;
      }

      const errorMessage = extractErrorMessage(err);
      if (optionsRef.current.showErrorEnabled !== false) {
        showError(errorMessage);
      }
      optionsRef.current.onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refetch = useCallback(async () => {
    if (debounceTimeoutRef.current !== null) {
      window.clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = undefined;
    }
    await fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!enabled) {
      if (debounceTimeoutRef.current != null) {
        window.clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = undefined;
      }
      return () => {};
    }

    if (debounceMs != null && debounceMs > 0) {
      if (debounceTimeoutRef.current != null) {
        window.clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = window.setTimeout(() => {
        debounceTimeoutRef.current = undefined;
        fetchData();
      }, debounceMs) as unknown as number;
      return () => {
        if (debounceTimeoutRef.current != null) {
          window.clearTimeout(debounceTimeoutRef.current);
          debounceTimeoutRef.current = undefined;
        }
      };
    }

    fetchData();
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, debounceMs, ...dependencies]);

  useEffect(
    () => () => {
      if (debounceTimeoutRef.current != null) {
        window.clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = undefined;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    },
    [],
  );

  return {
    isLoading,
    refetch,
    data,
  };
}

export default useApiRequest;
